import { defineNuxtPlugin } from 'nuxt/app';
import type { Ninetailed } from '@ninetailed/experience.js';
import type { EventType, Feature } from '@ninetailed/experience.js-shared';
import type { NinetailedPlugin } from '@ninetailed/experience.js-plugin-analytics';
import type { ExposedAudienceDefinition } from '@ninetailed/experience.js-preview-bridge';
import type {
  AudienceEntryLike,
  ExperienceEntryLike
} from '@ninetailed/experience.js-utils-contentful';
import { calculateHead, calculateTitle } from '~/composables/useMetadata';
import { buildUrlString } from '~/utils/buildUrlString';
import { pickNinetailedIdentifyTraits } from '~/utils/ninetailed/pickNinetailedIdentifyTraits';
import { buildPreferencesIdentifyTraits } from '~/utils/ninetailed/buildPreferencesIdentifyTraits';
import { useCustomerPreferencesStore } from '~/stores/customerPreferencesStore/customerPreferencesStore';
import { extractTopicData } from '~/utils/analytics/extractTopicData.ts';
import { waitForDocumentTitle } from '~/utils/ninetailed/waitForDocumentTitle';
import { useContentfulPayloadForRoute } from '~/composables/useContentfulPayloadForRoute';

type NinetailedPrivacyPluginConfig = {
  allowedEvents: EventType[];
  allowedTrackEvents: string[];
  allowedTrackEventProperties: string[];
  allowedTraits: string[];
  enabledFeatures: Feature[];
};

const NINETAILED_PRE_CONSENT_CONFIG: NinetailedPrivacyPluginConfig = {
  allowedEvents: [],
  allowedTrackEvents: [],
  allowedTrackEventProperties: [],
  allowedTraits: [],
  enabledFeatures: []
};

const NINETAILED_POST_CONSENT_CONFIG: NinetailedPrivacyPluginConfig = {
  allowedEvents: [
    'track',
    'page',
    'identify',
    'component',
    'component_click',
    'component_hover',
    'screen',
    'alias',
    'group'
  ],
  allowedTrackEvents: ['*'],
  allowedTrackEventProperties: ['*'],
  allowedTraits: ['*'],
  enabledFeatures: ['location', 'ip-enrichment']
};

function syncNinetailedConsentFromCookiebot(): void {
  (
    window.ninetailed as { consent?: (accepted: boolean) => void } | undefined
  )?.consent?.(!!window.Cookiebot?.consent?.marketing);
}

/**
 * Non-blocking: provide a Ref immediately so Nuxt does not await the SDK chunk
 * before mounting. Dynamic imports keep Ninetailed out of the eager entry.
 */
export default defineNuxtPlugin(() => {
  const ninetailedRef = shallowRef<Ninetailed | null>(null);
  const ninetailedStickyAudienceId = ref<string | null>(null);

  void initNinetailed()
    .then((ninetailed) => {
      ninetailedRef.value = ninetailed;
      wireNinetailed(ninetailed, ninetailedStickyAudienceId);
    })
    .catch((err: unknown) => {
      useLogger().error('NINETAILED INIT ERROR', err);
    });

  return {
    provide: {
      ninetailed: ninetailedRef,
      ninetailedStickyAudienceId
    }
  };
});

async function initNinetailed(): Promise<Ninetailed> {
  const {
    public: {
      isPreviewEnabled,
      personalizationClientId,
      personalizationEnvironment
    }
  } = useRuntimeConfig();

  const [
    { Ninetailed },
    { NinetailedPrivacyPlugin },
    { NinetailedGoogleTagmanagerPlugin },
    { NinetailedInsightsPlugin }
  ] = await Promise.all([
    import('@ninetailed/experience.js'),
    import('@ninetailed/experience.js-plugin-privacy'),
    import('@ninetailed/experience.js-plugin-google-tagmanager'),
    import('@ninetailed/experience.js-plugin-insights')
  ]);

  /**
   * On preview stages the personalization preview widget needs a profile to
   * evaluate audiences against, so consent must not gate anything there.
   * Granting consent via `window.ninetailed.consent()` is racy — the privacy
   * plugin exposes that method only after its own initialization — so the
   * pre-consent config is replaced instead.
   */
  const ninetailedPrivacyPlugin = new NinetailedPrivacyPlugin(
    isPreviewEnabled
      ? NINETAILED_POST_CONSENT_CONFIG
      : NINETAILED_PRE_CONSENT_CONFIG,
    NINETAILED_POST_CONSENT_CONFIG
  );

  const plugins: NinetailedPlugin[] = [
    ninetailedPrivacyPlugin,
    new NinetailedGoogleTagmanagerPlugin(),
    new NinetailedInsightsPlugin()
  ];

  if (isPreviewEnabled) {
    /**
     * Fixes write access to global window from "krakenjs/post-robot" via
     * PreviewBridge / NinetailedPreviewPlugin.
     * https://github.com/krakenjs/post-robot/blob/main/src/global.js#L88
     */
    (
      window as unknown as { WINDOW_WILDCARD: Record<string, any> }
    ).WINDOW_WILDCARD = {};

    const [
      { NinetailedPreviewPlugin },
      { patchNinetailedPreviewAudienceDeactivation }
    ] = await Promise.all([
      import('@ninetailed/experience.js-plugin-preview'),
      import('~/utils/ninetailed/patchNinetailedPreviewAudienceDeactivation')
    ]);

    const ninetailedPreviewPlugin = new NinetailedPreviewPlugin({
      experiences: await getAllExperiences(),
      audiences: await getAllAudiences(),
      onOpenExperienceEditor: (exp) => {
        const { $contentfulUrl } = useNuxtApp();
        window.open(`${$contentfulUrl.value}entries/${exp.id}`, '_blank');
      },
      onOpenAudienceEditor: (aud) => {
        const { $contentfulUrl } = useNuxtApp();
        window.open(`${$contentfulUrl.value}entries/${aud.id}`, '_blank');
      },
      ui: {
        opener: { hide: false }
      }
    });

    patchNinetailedPreviewAudienceDeactivation(
      ninetailedPreviewPlugin,
      useLogger()
    );
    plugins.push(ninetailedPreviewPlugin);
  }

  return new Ninetailed(
    {
      clientId: personalizationClientId,
      environment: personalizationEnvironment
    },
    {
      onError: (err: string | Error): void => {
        useLogger().error('NINETAILED ERROR', err);
      },
      plugins: plugins,
      useSDKEvaluation: true
    }
  );
}

function wireNinetailed(
  ninetailed: Ninetailed,
  ninetailedStickyAudienceId: Ref<string | null>
): void {
  const nuxtApp = useNuxtApp();
  const {
    $contentfulPagePayload,
    $shopStore,
    $globalPageSettings,
    $activeLocale
  } = nuxtApp;
  const { user } = useShopAuth();
  const router = useRouter();
  const resolvePayloadForRoute = useContentfulPayloadForRoute();

  router.afterEach(async (to, from) => {
    try {
      if (user.value?.claims?.oid) {
        try {
          const oid = user.value.claims.oid.toString();
          const traits = pickNinetailedIdentifyTraits(
            user.value.claims as Record<string, unknown>
          );
          const preferencesStore = useCustomerPreferencesStore();
          const preferencesTraits = preferencesStore.hasLoaded
            ? buildPreferencesIdentifyTraits(
                preferencesStore.answers,
                preferencesStore.onboardingData
              )
            : {};

          ninetailed.identify(oid, {
            loginStatus: 'loggedIn',
            user_id: oid,
            ...traits,
            ...preferencesTraits
          });
        } catch (e) {
          useLogger().error('Ninetailed identify error', e);
        }
      } else {
        try {
          const GoogleTagManagerUid = useCookie('_ga');
          if (GoogleTagManagerUid.value) {
            ninetailed.identify(GoogleTagManagerUid.value, {
              loginStatus: 'Guest',
              user_id: GoogleTagManagerUid.value
            });
          }
        } catch (e) {
          useLogger().error('Ninetailed identify error', e);
        }
      }

      // The shared payload may still belong to the previous page; only use it
      // once it matches `to`.
      const result = await resolvePayloadForRoute(to);
      if (result.status === 'mismatch') {
        console.warn(
          `Discard the ninetailed page event for route »${to.path}«`
        );
        return;
      }

      const page =
        result.status === 'matched' ? result.payload.page : undefined;
      const topicSlugMapEn =
        result.status === 'matched'
          ? (result.payload.topicSlugMapEn ?? {})
          : {};

      const q: Record<string, string> = {};
      Object.entries(to.query).forEach(([key, value]) => {
        q[key] = String(value);
      });

      const topicData = page
        ? extractTopicData(
            page,
            $activeLocale.value.contentfulCode,
            topicSlugMapEn
          )
        : {};

      const title = calculateTitle(
        page,
        $globalPageSettings?.value?.companyName ?? ''
      );

      // Ninetailed overwrites `title` with `document.title`, which is updated
      // by `useHead` only after the new page has rendered.
      await waitForDocumentTitle(title);

      ninetailed.page({
        path: to.path,
        query: q,
        referrer: from.fullPath,
        search: to.query.toString(),
        url: to.fullPath,
        ...topicData
      });
    } catch (e) {
      useLogger().error('Ninetailed page error', e);
    }
  });

  watch(
    () => $contentfulPagePayload?.value?.page?.sys?.id,
    () => {
      ninetailedStickyAudienceId.value = null;

      ninetailed.track('page_load', {
        ecom_region: $shopStore?.value,
        page_format: $contentfulPagePayload?.value?.page?.fields?.format ?? '',
        page_path_en: buildUrlString(
          'en',
          $contentfulPagePayload?.value?.localeSlugMap?.en
        )
      });
    },
    { immediate: true }
  );

  window.addEventListener(
    'CookiebotOnConsentReady',
    syncNinetailedConsentFromCookiebot
  );
  window.addEventListener(
    'CookiebotOnAccept',
    syncNinetailedConsentFromCookiebot
  );
  window.addEventListener(
    'CookiebotOnDecline',
    syncNinetailedConsentFromCookiebot
  );

  // Init may finish after app:mounted — sync now, and again on mount if later.
  syncNinetailedConsentFromCookiebot();
  nuxtApp.hook('app:mounted', async () => {
    await nextTick();
    syncNinetailedConsentFromCookiebot();
  });
}

const getAllExperiences = async () => {
  const { $activeLocale } = useNuxtApp();
  const { ExperienceMapper } =
    await import('@ninetailed/experience.js-utils-contentful');

  const entries = await $fetch('/api/contentful/byQuery', {
    method: 'POST',
    query: {
      locale: $activeLocale.value.webapp
    },
    body: {
      query: {
        content_type: 'nt_experience',
        include: 1
      }
    }
  });

  return (entries as ExperienceEntryLike[])
    .filter((entry) => {
      return ExperienceMapper.isExperienceEntry(entry);
    })
    .map((entry) => ExperienceMapper.mapExperience(entry));
};

const getAllAudiences = async () => {
  const { $activeLocale } = useNuxtApp();
  const { AudienceMapper } =
    await import('@ninetailed/experience.js-utils-contentful');

  const entries = await $fetch('/api/contentful/byQuery', {
    method: 'POST',
    query: {
      locale: $activeLocale.value.webapp
    },
    body: {
      query: {
        content_type: 'nt_audience',
        include: 1
      }
    }
  });

  return (entries as AudienceEntryLike[])
    .filter(AudienceMapper.isAudienceEntry)
    .map(AudienceMapper.mapAudience) as ExposedAudienceDefinition[];
};
